import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { getGameById, getSimilarGames } from '../data/gamesRegistry';
import gamesFeed from '../data/gamesFeed.json';

export const WikiPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  
  let game = getGameById(id || '');
  if (!game && id) {
    game = (gamesFeed as any[]).find(g => g.id === id);
  }

  if (!game) {
    return (
      <div style={{ padding: '100px 20px', textAlign: 'center' }}>
        <h2>Wiki Entry Not Found</h2>
        <Link to="/" style={{ color: '#3b82f6', textDecoration: 'none' }}>Return to Arcade</Link>
      </div>
    );
  }

  const similarGames = getSimilarGames(game.category, game.id, 4);

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px', color: '#111827', minHeight: '80vh' }}>
      <Helmet>
        <title>{game.title} Wiki & Guide - How to Play | Gamezolve</title>
        <meta name="description" content={`Complete wiki, strategy guide, and how-to-play instructions for ${game.title}. Master this ${game.category} game today on Gamezolve!`} />
        <meta name="keywords" content={`${game.title} wiki, ${game.title} guide, how to play ${game.title}, ${game.title} strategies, ${game.category} games`} />
        
        {/* OpenGraph / Social Meta Tags */}
        <meta property="og:title" content={`${game.title} Wiki & Guide | Gamezolve`} />
        <meta property="og:description" content={`Complete wiki, strategy guide, and how-to-play instructions for ${game.title}.`} />
        <meta property="og:image" content={game.thumbnail} />
        <meta property="og:type" content="article" />
      </Helmet>

      {/* Header */}
      <div style={{ display: 'flex', gap: '30px', alignItems: 'center', marginBottom: '40px', flexWrap: 'wrap' }}>
        <img 
          src={game.thumbnail} 
          alt={game.title} 
          style={{ width: '150px', height: '150px', borderRadius: '20px', objectFit: 'cover', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }} 
        />
        <div>
          <h1 style={{ fontSize: '3rem', margin: '0 0 10px 0', fontFamily: 'var(--font-family-game, inherit)' }}>
            {game.title} Wiki
          </h1>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <span style={{ background: '#e5e7eb', padding: '5px 15px', borderRadius: '20px', fontWeight: 'bold' }}>
              {game.category}
            </span>
            <Link to={`/game/${game.id}`} style={{ 
              background: '#3b82f6', 
              color: 'white', 
              padding: '8px 20px', 
              borderRadius: '20px', 
              textDecoration: 'none', 
              fontWeight: 'bold',
              boxShadow: '0 4px 10px rgba(59, 130, 246, 0.3)'
            }}>
              Play Game
            </Link>
          </div>
        </div>
      </div>

      <hr style={{ border: 'none', borderTop: '2px solid #e5e7eb', marginBottom: '40px' }} />

      {/* Overview */}
      <section style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '20px', color: '#1f2937' }}>Overview</h2>
        <div style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#4b5563' }} dangerouslySetInnerHTML={{ __html: game.descriptionHtml || game.description || '' }} />
      </section>

      {/* How to Play */}
      <section style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '20px', color: '#1f2937' }}>How to Play {game.title}</h2>
        <div style={{ background: '#f3f4f6', padding: '30px', borderRadius: '15px', borderLeft: '5px solid #3b82f6' }}>
          {game.instructions ? (
            <p style={{ fontSize: '1.1rem', lineHeight: '1.7', margin: 0, color: '#374151' }}>{game.instructions}</p>
          ) : (
            <p style={{ fontSize: '1.1rem', lineHeight: '1.7', margin: 0, color: '#374151' }}>
              Dive right into the action! The mechanics of {game.title} are designed to be intuitive. Use your mouse, touch screen, or keyboard to interact with the game elements and discover the winning strategy.
            </p>
          )}
        </div>
      </section>

      {/* Game Details */}
      <section style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '20px', color: '#1f2937' }}>Game Details</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1.1rem' }}>
          <tbody>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '15px 0', fontWeight: 'bold', color: '#6b7280', width: '200px' }}>Genre</td>
              <td style={{ padding: '15px 0' }}>{game.category}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '15px 0', fontWeight: 'bold', color: '#6b7280' }}>Platform</td>
              <td style={{ padding: '15px 0' }}>Web Browser (Desktop, Mobile, Tablet)</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '15px 0', fontWeight: 'bold', color: '#6b7280' }}>Technology</td>
              <td style={{ padding: '15px 0' }}>{game.technology || 'HTML5'}</td>
            </tr>
            {(game.tags && game.tags.length > 0) && (
              <tr>
                <td style={{ padding: '15px 0', fontWeight: 'bold', color: '#6b7280' }}>Tags</td>
                <td style={{ padding: '15px 0' }}>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {(game.tags as string[]).map((t, idx) => (
                      <span key={idx} style={{ background: '#e5e7eb', padding: '4px 10px', borderRadius: '10px', fontSize: '0.9rem' }}>
                        #{t}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {/* Similar Games */}
      {similarGames.length > 0 && (
        <section>
          <h2 style={{ fontSize: '2rem', marginBottom: '20px', color: '#1f2937' }}>Related Games</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '20px' }}>
            {similarGames.map((sg: any) => (
              <Link key={sg.id} to={sg.path} style={{ textDecoration: 'none', color: 'inherit' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <img src={sg.thumbnail} alt={sg.title} style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', borderRadius: '15px', backgroundColor: '#e5e7eb' }} />
                  <span style={{ fontWeight: 'bold' }}>{sg.title}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

    </div>
  );
};
